import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/common/Select";
import type { SelectOption } from "components/common/Select/types";
import { PlanArchivedBadge } from "../PlanArchivedBadge";
import type { ComparedPlanSelectProps } from "./types";

export const ComparedPlanSelect = ({
  candidateRecords,
  comparedPlanRecord,
  onComparedPlanSelect,
  className,
}: ComparedPlanSelectProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      candidateRecords.map((record) => ({
        value: String(record.id),
        label: record.name,
        endAdornment:
          record.archivedAt !== null ? <PlanArchivedBadge /> : undefined,
      })),
    [candidateRecords],
  );

  const filterOption = useCallback(
    (input: string, option: SelectOption) => {
      // A committed pick fills the input; show every option again then.
      const isCommittedLabel = candidateRecords.some(
        (record) => record.name === input,
      );
      if (isCommittedLabel) return true;
      return option.label.toLowerCase().includes(input.toLowerCase());
    },
    [candidateRecords],
  );

  const handleChange = useCallback(
    (value: string) => {
      // The Select also emits free filter text; only commit real option picks.
      const pickedRecord = candidateRecords.find(
        (record) => String(record.id) === value,
      );
      if (!pickedRecord) return;
      onComparedPlanSelect(pickedRecord.id);
    },
    [candidateRecords, onComparedPlanSelect],
  );

  return (
    <Select
      options={options}
      value={
        comparedPlanRecord !== null ? String(comparedPlanRecord.id) : undefined
      }
      onChange={handleChange}
      placeholder={t("smartSurveyor.planComparison.selectPlanPlaceholder")}
      autoFilter={{ filterOption }}
      className={className}
    />
  );
};
